import { truncate } from "lodash";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { CitationFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import { assessmentTypeSectionHeadings } from "@apps/assessments/helpers";
import {
  canOpenInSidebar,
  getInternalPathname,
} from "@apps/main/routes-constants";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import IntegrationLogo from "@components/integration-logo/integration-logo";
import AppLink from "@components/link/link";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { removeCurrentYear, toWithBackground } from "@helpers/helpers";

/**
 * A single "source used" row: a provider icon (reviewer avatar / integration
 * logo / external-link fallback) followed by the linked, type-prefixed title
 * and the source date, plus an optional private-note excerpt.
 *
 * Shared by the citations modal and the inline "N sources used" disclosure so
 * both render identical rows. `citation.sourceProvider` is expected to already
 * be rewritten to the review label by the caller for past reviews.
 */
const AskAICitationRow = ({
  citation,
  onNavigate,
}: {
  citation: CitationFragmentFragment;
  /** Called when an in-app link is clicked (e.g. to close the modal). */
  onNavigate?: () => void;
}) => {
  const label = useLabel();
  const location = useLocation<TFLocationState>();
  const reviewSourceProvider = label("review", { pluralize: true });

  const hasUrl = !!citation.url;
  const showSidebar = hasUrl && canOpenInSidebar(citation.url);

  const icon =
    citation.sourceProvider === reviewSourceProvider &&
    citation.assessment?.responder ? (
      <Avatar
        user={citation.assessment.responder}
        className="float-left mr-1.5 mt-0.5 w-4 h-4"
      />
    ) : citation.sourceProvider ? (
      <IntegrationLogo
        provider={citation.sourceProvider}
        className="float-left mr-1.5 mt-0.5 w-4 h-4"
      />
    ) : (
      <TopicflowIcon
        type="externalLink"
        className="float-left mr-1.5 mt-0.5 w-4 h-4"
      />
    );

  const linkContent = (
    <>
      {citation.sourceProvider === reviewSourceProvider &&
      citation.assessment?.template.assessmentType
        ? `${
            assessmentTypeSectionHeadings(
              label,
              false,
              citation.assessment?.target?.id ===
                citation.assessment?.responder?.id,
            )[
              citation.assessment.template.assessmentType as keyof ReturnType<
                typeof assessmentTypeSectionHeadings
              >
            ]
          }: `
        : citation.sourceProvider === "topicflow"
          ? `${label(citation.sourceType.split("_").join(" "), {
              titleCase: true,
            })}: `
          : ""}
      {citation.sourceProvider === reviewSourceProvider &&
      citation.assessment?.responder?.name
        ? citation.assessment.responder.name
        : truncate(citation.title, { length: 100, separator: " " })}
      {citation.meeting && (
        <span className="text-gray-400 ml-1">
          (
          {removeCurrentYear(
            moment(citation.meeting.startDatetime).format("ll"),
          )}
          )
        </span>
      )}
      {citation.assessment?.submittedDatetime && (
        <span className="text-gray-400 ml-1">
          (
          {removeCurrentYear(
            moment(citation.assessment.submittedDatetime).format("ll"),
          )}
          )
        </span>
      )}
    </>
  );

  return (
    <div className="flex items-start">
      {icon}
      <div className="min-w-0">
        {showSidebar ? (
          <AppLink
            to={toWithBackground({
              pathname: getInternalPathname(citation.url) || "",
              location,
            })}
            className="text-gray-700 hover:underline"
            onClick={onNavigate}
          >
            {linkContent}
          </AppLink>
        ) : hasUrl ? (
          <a
            href={citation.url}
            className="text-gray-700 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkContent}
          </a>
        ) : (
          <span className="text-gray-700">{linkContent}</span>
        )}
        {citation.sourceType === "private_note" && citation.contextText && (
          <p className="mt-1 text-sm text-gray-500">{citation.contextText}</p>
        )}
      </div>
    </div>
  );
};

export default AskAICitationRow;
